import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, filter } from 'rxjs';

import { WebsocketService } from '@core/services/websocket.service';
import { CONFIG_TOKEN } from '@core/tokens';

import { AlertWebsocketMessage, AlertsBySiteRequest, AlertsRequest, AlertsResponse, DismissAlertRequest } from './alerting.models';

@Injectable({
  providedIn: 'root',
})
export class AlertingService {
  readonly #ROOT_URL = inject(CONFIG_TOKEN).backendRootUrl;

  readonly #httpClient = inject(HttpClient);
  readonly #websocketService = inject(WebsocketService);

  public getAlerts$(request?: AlertsRequest): Observable<AlertsResponse> {
    return this.#httpClient.get<AlertsResponse>(`${this.#ROOT_URL}/api/v1/alerts/sites`, {
      params: this.getParamsFromAlertsRequest(request),
    });
  }

  public getAlertsForSite$(siteId: string, request?: AlertsBySiteRequest): Observable<AlertsResponse> {
    return this.#httpClient.get<AlertsResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/alerts`, {
      params: this.getParamsFromAlertsBySiteRequest(request),
    });
  }

  public dismissAlert$(siteId: string, alertId: string, request: DismissAlertRequest): Observable<void> {
    return this.#httpClient.post<void>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/alerts/${alertId}/dismissed`, request);
  }

  public resumeAlert$(siteId: string, alertId: string): Observable<void> {
    return this.#httpClient.delete<void>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/alerts/${alertId}/dismissed`);
  }

  public getAlertsWebsocketMessage$(request?: AlertsRequest): Observable<AlertWebsocketMessage> {
    const params = this.getParamsFromAlertsRequest(request);
    let paramsString = params.toString();
    if (paramsString) {
      paramsString = `?${paramsString}`;
    }

    const topic = `alert${paramsString}`;

    return this.#websocketService.connectToTopic$<AlertWebsocketMessage>(topic).pipe(filter((message) => message.topic === topic));
  }

  public getAlertsForSiteWebsocketMessage$(siteId: string, request?: AlertsBySiteRequest): Observable<AlertWebsocketMessage> {
    const params = this.getParamsFromAlertsBySiteRequest(request);
    let paramsString = params.toString();
    if (paramsString) {
      paramsString = `?${paramsString}`;
    }

    const topic = `alert/${siteId}${paramsString}`;

    return this.#websocketService.connectToTopic$<AlertWebsocketMessage>(topic).pipe(filter((message) => message.topic === topic));
  }

  private getParamsFromAlertsRequest(request?: AlertsRequest): HttpParams {
    let params = new HttpParams();
    if (request?.siteIds) {
      request.siteIds.forEach((siteId) => {
        params = params.append('site', siteId);
      });
    }
    if (request?.tenantIds) {
      request.tenantIds.forEach((tenantId) => {
        params = params.append('tenant', tenantId);
      });
    }
    if (request?.states) {
      request.states.forEach((state) => {
        params = params.append('state', state);
      });
    }
    if (request?.start) {
      const startISO = DateTime.fromISO(request.start).startOf('day').toISO();

      if (startISO) {
        params = params.append('start', startISO);
      }
    }
    if (request?.end) {
      const endISO = DateTime.fromISO(request.end).endOf('day').toISO();
      if (endISO) {
        params = params.append('end', endISO);
      }
    }
    if (request?.withSopOnly) {
      params = params.append('withSopOnly', 'true');
    }
    request?.chargerIds?.forEach((chargerId) => {
      params = params.append('charger', chargerId);
    });
    request?.evseIds?.forEach((evseId) => {
      params = params.append('evse', evseId);
    });
    request?.vehicleIds?.forEach((vehicleId) => {
      params = params.append('vehicle', vehicleId);
    });

    return params;
  }

  private getParamsFromAlertsBySiteRequest(request?: AlertsBySiteRequest): HttpParams {
    let params = new HttpParams();
    if (request?.states) {
      request.states.forEach((state) => {
        params = params.append('state', state);
      });
    }
    if (request?.start) {
      const startISO = DateTime.fromISO(request.start).startOf('day').toISO();

      if (startISO) {
        params = params.append('start', startISO);
      }
    }
    if (request?.end) {
      const endISO = DateTime.fromISO(request.end).endOf('day').toISO();
      if (endISO) {
        params = params.append('end', endISO);
      }
    }
    if (request?.withSopOnly) {
      params = params.append('withSopOnly', 'true');
    }
    request?.chargerIds?.forEach((chargerId) => {
      params = params.append('charger', chargerId);
    });
    request?.evseIds?.forEach((evseId) => {
      params = params.append('evse', evseId);
    });
    request?.vehicleIds?.forEach((vehicleId) => {
      params = params.append('vehicle', vehicleId);
    });

    return params;
  }
}
